import actions, { Actions } from '../../../constants/actions';

export type Permission = {
  action: Actions;
  description: string;
  extraInformation: string;
}

export type PermissionCategory = {
  name: string;
  subCategories: {
    name: string;
    permissions: Permission[];
  }[];
}

const permissionCategories: PermissionCategory[] = [
  {
    name: 'Risk monitoring',
    subCategories: [
      {
        name: 'Cases',
        permissions: [
          {
            action: actions.CASE_ADD_COMMENT,
            description: 'Add comments to cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_DELETE_COMMENT,
            description: 'Delete comments from cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_ASSIGN,
            description: 'Assign a person to cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_CREATE,
            description: 'Create cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_DOWNLOAD,
            description: 'Download case reports',
            extraInformation: '',
          },
          {
            action: actions.CASE_ESCALATE,
            description: 'Escalate cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_GET,
            description: 'View case details',
            extraInformation: '',
          },
          {
            action: actions.CASE_GET_STATUS_BREAKDOWN,
            description: 'View status breakdown in case',
            extraInformation: '',
          },
          {
            action: actions.CASE_LIST,
            description: 'View case lists',
            extraInformation: '',
          },
          {
            action: actions.CASE_MANAGE_ENTITIES,
            description: 'Add/delete entities on case',
            extraInformation: '',
          },
          {
            action: actions.CASE_SUMMARY_CREATE,
            description: 'View AI-generated case summaries',
            extraInformation: '',
          },
          {
            action: actions.CASE_UPDATE_PRIORITY,
            description: 'Update priority of a case',
            extraInformation: '',
          },
          {
            action: actions.CASE_UPDATE_STATUS,
            description: 'Update status on case',
            extraInformation: '',
          },
          {
            action: actions.CASE_UPDATE,
            description: 'Edit cases details',
            extraInformation: '',
          },
          {
            action: actions.ANALYSIS_CREATE,
            description: 'Create case AI analysis',
            extraInformation: '',
          },
          {
            action: actions.ANALYSIS_GET,
            description: 'Get case AI analysis',
            extraInformation: '',
          },
          {
            action: actions.ANALYSIS_REVISE,
            description: 'Revise case AI analysis',
            extraInformation: '',
          },
          {
            action: actions.ANALYSIS_ADD_TO_COMMENTS,
            description: 'Add case AI analysis to comments',
            extraInformation: '',
          },
          {
            action: actions.CASE_TRANSACTION_SNAPSHOT_GET,
            description: 'Viewing snapshot details (API)',
            extraInformation: '',
          },
          {
            action: actions.CASE_TRANSACTION_SNAPSHOT_LIST,
            description: 'View snapshots in cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_TRANSACTION_SNAPSHOT_CREATE,
            description: 'Create snapshots in cases',
            extraInformation: '',
          },
          {
            action: actions.CASE_TRANSACTION_SNAPSHOT_DELETE,
            description: 'Delete snapshots in cases',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Pings',
        permissions: [
          {
            action: actions.PING_ADD_COMMENT,
            description: 'Add comments to pings',
            extraInformation: '',
          },
          {
            action: actions.PING_DELETE_COMMENT,
            description: 'Delete comments from pings',
            extraInformation: '',
          },
          {
            action: actions.PING_CREATE,
            description: 'Create pings',
            extraInformation: '',
          },
          {
            action: actions.PING_GET,
            description: 'View ping details',
            extraInformation: '',
          },
          {
            action: actions.PING_LIST,
            description: 'View ping lists',
            extraInformation: '',
          },
          {
            action: actions.PING_UPDATE_STATUS,
            description: 'Update status on ping',
            extraInformation: '',
          },
          {
            action: actions.PING_UPDATE,
            description: 'Edit ping details (add tags)',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Requests',
        permissions: [
          {
            action: actions.REQUEST_ADD_COMMENT,
            description: 'Add comments to requests',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_DELETE_COMMENT,
            description: 'Delete comments from requests',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_CREATE,
            description: 'Create requests',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_GET,
            description: 'View request details',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_LIST,
            description: 'View request lists',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_REPROCESS,
            description: 'Reprocess requests',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_UPDATE_RECOMMENDATION,
            description: 'Update recommendation on requests',
            extraInformation: '',
          },
          {
            action: actions.REQUEST_UPDATE,
            description: 'Edit request details (API)',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Reports',
        permissions: [
          {
            action: actions.REPORT_CREATE,
            description: 'Generate reports',
            extraInformation: '',
          },
          {
            action: actions.REPORT_DOWNLOAD,
            description: 'Download reports',
            extraInformation: '',
          },
          {
            action: actions.REPORT_GET,
            description: 'View report details (API)',
            extraInformation: '',
          },
          {
            action: actions.REPORT_LIST,
            description: 'View report lists',
            extraInformation: '',
          },
        ],
      },
    ],
  },
  {
    name: 'Entities',
    subCategories: [
      {
        name: 'Accounts',
        permissions: [
          {
            action: actions.ACCOUNT_ENTITY_CREATE,
            description: 'Create accounts in the system',
            extraInformation: '',
          },
          {
            action: actions.ACCOUNT_ENTITY_GET,
            description: 'View account details',
            extraInformation: '',
          },
          {
            action: actions.ACCOUNT_ENTITY_LIST,
            description: 'View account lists',
            extraInformation: '',
          },
          {
            action: actions.ACCOUNT_ENTITY_UPDATE,
            description: 'Edit account details',
            extraInformation: 'Edit existing account, update account holders, add custom connection, add connected products, update group assignments on accounts',
          },
        ],
      },
      {
        name: 'Businesses',
        permissions: [
          {
            action: actions.BUSINESS_CREATE_ADVERSE_MEDIA,
            description: 'Purchase adverse media data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_COMPANY_REP,
            description: 'Purchase company representation data on businesses ',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_CREDIT,
            description: 'Purchase credit report data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_FINANCIAL_DATA,
            description: 'Purchase financial data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_GENERAL_INFO,
            description: 'Purchase general information data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_OWNERSHIP,
            description: 'Purchase ownership data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_SANCTION_LIST_COMPANY,
            description: 'Purchase sanction list data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE,
            description: 'Create businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_ADVERSE_MEDIA,
            description: 'View adverse media data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_COMPANY_REP,
            description: 'View company representation data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_CREDIT,
            description: 'View credit report data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_FINANCIAL_DATA,
            description: 'View financial data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_GENERAL_INFO,
            description: 'View general information data on businesses ',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_OWNERSHIP,
            description: 'View ownership data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_SANCTION_LIST_COMPANY,
            description: 'View sanction data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_TRANSACTIONS,
            description: 'View transaction data on businesses',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET,
            description: 'View business details',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_LIST,
            description: 'View business lists',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_UPDATE,
            description: 'Edit business details',
            extraInformation: 'Edit existing businesses, add Connect products, add Connected accounts, update Group assignment on businesses',
          },
          {
            action: actions.BUSINESS_CREATE_EXPOSURE,
            description: 'Create business exposure',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_EXPOSURE,
            description: 'Get business exposure',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_CREATE_IP_ADDRESS,
            description: 'Create business IP address',
            extraInformation: '',
          },
          {
            action: actions.BUSINESS_GET_IP_ADDRESS,
            description: 'Get business IP address',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Individuals',
        permissions: [
          {
            action: actions.INDIVIDUAL_CREATE_ADVERSE_MEDIA,
            description: 'Purchase adverse media data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_BUSINESS_ASSOCIATION,
            description: 'Purchase business association data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_CREDIT,
            description: 'Purchase credit score data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_IP_ADDRESS,
            description: 'Purchase IP data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_LEGAL_SCORE,
            description: 'Purchase criminal record data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_PEP,
            description: 'Purchase PEP data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_SANCTION_LIST,
            description: 'Purchase sanction data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE_SPAR,
            description: 'Purchase population register (SPAR) data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_CREATE,
            description: 'Create individuals in the system',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_ADVERSE_MEDIA,
            description: 'View adverse media data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_BUSINESS_ASSOCIATION,
            description: 'View business association data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_CREDIT,
            description: 'View credit score data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_IP_ADDRESS,
            description: 'View IP data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_LEGAL_SCORE,
            description: 'View criminal record data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_PEP,
            description: 'View PEP data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_SANCTION_LIST,
            description: 'View sanction data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_SPAR,
            description: 'View population register (SPAR) data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET_TRANSACTIONS,
            description: 'View transaction data on individuals',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_GET,
            description: 'View individual details',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_LIST,
            description: 'View individual lists',
            extraInformation: '',
          },
          {
            action: actions.INDIVIDUAL_UPDATE,
            description: 'Edit individual details',
            extraInformation: 'Edit existing individuals, add connect products, add connected accounts, update group assignment on individuals',
          },
        ],
      },
      {
        name: 'Products',
        permissions: [
          {
            action: actions.PRODUCT_CREATE,
            description: 'Create products',
            extraInformation: '',
          },
          {
            action: actions.PRODUCT_GET,
            description: 'View product details',
            extraInformation: '',
          },
          {
            action: actions.PRODUCT_LIST,
            description: 'View product lists',
            extraInformation: '',
          },
          {
            action: actions.PRODUCT_UPDATE,
            description: 'Edit product details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Cars',
        permissions: [
          {
            action: actions.CAR_CREATE_BASIC_DATA,
            description: 'Purchase basic data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_CREATE_EXEMPTION_DATA,
            description: 'Purchase exemption data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_CREATE_PREVIOUS_OWNERS_DATA,
            description: 'Purchase previous owner data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_CREATE_TECHNICAL_DATA,
            description: 'Purchase technical data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_CREATE_VEHICLE_INSPECTION_DATA,
            description: 'Purchase inspection data  on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_CREATE,
            description: 'Create cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET_BASIC_DATA,
            description: 'View basic data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET_EXEMPTION_DATA,
            description: 'View exemption data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET_PREVIOUS_OWNERS_DATA,
            description: 'View previous owner data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET_TECHNICAL_DATA,
            description: 'View technical data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET_VEHICLE_INSPECTION_DATA,
            description: 'View inspection data on cars',
            extraInformation: '',
          },
          {
            action: actions.CAR_GET,
            description: 'View car details',
            extraInformation: '',
          },
          {
            action: actions.CAR_LIST,
            description: 'View car lists',
            extraInformation: '',
          },
          {
            action: actions.CAR_UPDATE,
            description: 'Edit car details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Forms',
        permissions: [
          {
            action: actions.DATA_POINT_GET,
            description: 'View form question details',
            extraInformation: '',
          },
          {
            action: actions.DATA_POINT_CREATE,
            description: 'Add new information manually',
            extraInformation: '',
          },
          {
            action: actions.DATA_POINT_UPDATE,
            description: 'Resolve information conflicts',
            extraInformation: '',
          },
          {
            action: actions.DATA_POINT_LIST,
            description: 'See information conflicts',
            extraInformation: '',
          },
          {
            action: actions.FORM_UPDATE,
            description: 'Update form setups (API)',
            extraInformation: '',
          },
          {
            action: actions.FORM_STUDIO_ACCESS,
            description: 'Access Form Studio',
            extraInformation: '',
          },
          {
            action: actions.FORM_ANSWER_GET,
            description: 'View form answers',
            extraInformation: '',
          },
          {
            action: actions.FORM_ANSWER_SUBMIT,
            description: 'Submitting forms (API)',
            extraInformation: '',
          },
          {
            action: actions.FORM_ANSWER_UPDATE,
            description: 'Edit form answers',
            extraInformation: '',
          },
          {
            action: actions.FORM_CREATE,
            description: 'Setup forms (API)',
            extraInformation: '',
          },
          {
            action: actions.FORM_GET,
            description: 'View form setup details',
            extraInformation: '',
          },
          {
            action: actions.FORM_LIST,
            description: 'View form setup lists',
            extraInformation: '',
          },
          {
            action: actions.FORM_GET_LINK,
            description: 'Access form links',
            extraInformation: '',
          },
          {
            action: actions.RESOLUTION_HISTORY_LIST,
            description: 'View conflicts resolution history lists',
            extraInformation: '',
          },
          {
            action: actions.RESOLUTION_HISTORY_GET,
            description: 'View conflicts resolution history details',
            extraInformation: '',
          },
          {
            action: actions.RESOLUTION_HISTORY_CREATE,
            description: 'Create conflict resolutions (API)',
            extraInformation: '',
          },
          {
            action: actions.RESOLUTION_HISTORY_UPDATE,
            description: 'Update conflict resolutions (API)',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Custom connections',
        permissions: [
          {
            action: actions.CONNECTION_CREATE,
            description: 'Add custom connections',
            extraInformation: '',
          },
          {
            action: actions.CONNECTION_LIST,
            description: 'View custom connections',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'IP addresses',
        permissions: [
          {
            action: actions.IP_USAGE_LIST,
            description: 'View IP addresses used by entities',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Device data and ElectronicID',
        permissions: [
          {
            action: actions.DEVICE_USAGE_LIST,
            description: 'View device data and electronicID used by entities',
            extraInformation: '',
          },
        ],
      },
    ],
  },
  {
    name: 'Risk Setup',
    subCategories: [
      {
        name: 'Groups',
        permissions: [
          {
            action: actions.GROUP_CREATE,
            description: 'Create groups',
            extraInformation: '',
          },
          {
            action: actions.GROUP_GET,
            description: 'View group details',
            extraInformation: '',
          },
          {
            action: actions.GROUP_HISTORY_LIST,
            description: 'View the history of group assignments on entities',
            extraInformation: '',
          },
          {
            action: actions.GROUP_LIST,
            description: 'View group lists',
            extraInformation: '',
          },
          {
            action: actions.GROUP_UPDATE_ACTIVATION,
            description: 'Activate/deactivate groups',
            extraInformation: '',
          },
          {
            action: actions.GROUP_UPDATE,
            description: 'Edit group details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Processes',
        permissions: [
          {
            action: actions.PROCESS_CREATE,
            description: 'Create processes',
            extraInformation: '',
          },
          {
            action: actions.PROCESS_GET,
            description: 'View process details',
            extraInformation: '',
          },
          {
            action: actions.PROCESS_LIST,
            description: 'View process lists',
            extraInformation: '',
          },
          {
            action: actions.PROCESS_UPDATE_ACTIVATION,
            description: 'Activate/deactivate processes',
            extraInformation: '',
          },
          {
            action: actions.PROCESS_UPDATE,
            description: 'Edit process details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Risk classes',
        permissions: [
          {
            action: actions.RISK_CLASS_CREATE,
            description: 'Create risk classes',
            extraInformation: '',
          },
          {
            action: actions.RISK_CLASS_DELETE,
            description: 'Delete risk classes',
            extraInformation: '',
          },
          {
            action: actions.RISK_CLASS_GET,
            description: 'View risk class details',
            extraInformation: '',
          },
          {
            action: actions.RISK_CLASS_LIST,
            description: 'View risk class lists',
            extraInformation: '',
          },
          {
            action: actions.RISK_CLASS_UPDATE,
            description: 'Edit risk class details',
            extraInformation: '',
          },
          {
            action: actions.SCORE_LIST,
            description: 'View risk scores',
            extraInformation: 'View risk score in lists and on entities, view risk score distribution in dashboard',
          },
        ],
      },
      {
        name: 'Rules',
        permissions: [
          {
            action: actions.RULE_CREATE,
            description: 'Create rules',
            extraInformation: '',
          },
          {
            action: actions.RULE_DELETE,
            description: 'Delete rules',
            extraInformation: '',
          },
          {
            action: actions.RULE_GET,
            description: 'View rule details',
            extraInformation: '',
          },
          {
            action: actions.RULE_LIST,
            description: 'View rule lists, view rule tree',
            extraInformation: '',
          },
          {
            action: actions.RULE_UPDATE,
            description: 'Edit rules',
            extraInformation: 'Edit rule details, Reset rule frameworks',
          },
          {
            action: actions.RULE_VERSION_LIST,
            description: 'View rule version history',
            extraInformation: '',
          },
          {
            action: actions.RULE_VERSION_PUBLISH,
            description: 'Publish rule versions',
            extraInformation: 'Publish rule tree drafts as test or live versions',
          },
          {
            action: actions.RULE_VERSION_RELEASE,
            description: 'Release rule versions',
            extraInformation: 'Release a test rule version as live',
          },
          {
            action: actions.RULE_VERSION_APPROVE,
            description: 'Approve rule versions',
            extraInformation: 'Approve pending rule version reviews before they are published or released',
          },
          {
            action: actions.BANK_DATA_GET_CATEGORIZATION,
            description: 'Get bank data categorization',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Signals',
        permissions: [
          {
            action: actions.SIGNAL_CREATE,
            description: 'Create signals',
            extraInformation: '',
          },
          {
            action: actions.SIGNAL_GET,
            description: 'View signal details',
            extraInformation: '',
          },
          {
            action: actions.SIGNAL_LIST,
            description: 'View signal lists',
            extraInformation: '',
          },
          {
            action: actions.SIGNAL_UPDATE_ACTIVATION,
            description: 'Activate/deactivate signals',
            extraInformation: '',
          },
          {
            action: actions.SIGNAL_UPDATE,
            description: 'Edit signal details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Transaction types',
        permissions: [
          {
            action: actions.TRANSACTION_TYPE_CREATE,
            description: 'Create transaction types',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TYPE_GET,
            description: 'View transaction type details',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TYPE_LIST,
            description: 'View transaction type lists',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TYPE_UPDATE_ACTIVATION,
            description: 'Activate/deactivate transaction types',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TYPE_UPDATE,
            description: 'Edit transaction type details',
            extraInformation: '',
          },
        ],
      },
    ],
  },
  {
    name: 'Administration',
    subCategories: [
      {
        name: 'API Keys',
        permissions: [
          {
            action: actions.API_KEY_CREATE,
            description: 'Create API keys',
            extraInformation: '',
          },
          {
            action: actions.API_KEY_GET_SECRET,
            description: 'View key secrets',
            extraInformation: '',
          },
          {
            action: actions.API_KEY_GET,
            description: 'View API keys details',
            extraInformation: '',
          },
          {
            action: actions.API_KEY_LIST,
            description: 'View API keys lists',
            extraInformation: '',
          },
          {
            action: actions.API_KEY_UPDATE,
            description: 'Edit API keys',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Account security',
        permissions: [
          {
            action: actions.ACCOUNT_SECURITY_UPDATE,
            description: 'View and change all the account security settings',
            extraInformation: 'Covers every security feature that applies to all members of the account, such as requiring multi-factor authentication and the password policy',
          },
        ],
      },
      {
        name: 'Members',
        permissions: [
          {
            action: actions.MEMBER_CREATE,
            description: 'Create members',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_GET,
            description: 'View member details',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_INITIATE_MFA_RESET,
            description: 'Reset multi-factor authentication of members',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_INITIATE_RESET,
            description: 'Reset password of members',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_LIST,
            description: 'View member lists',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_UPDATE_ACTIVATION,
            description: 'Activate/deactivate members',
            extraInformation: '',
          },
          {
            action: actions.MEMBER_UPDATE,
            description: 'Edit member details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Permission roles',
        permissions: [
          {
            action: actions.PERMISSION_ROLE_CREATE,
            description: 'Create lists of permission roles (including in API keys)',
            extraInformation: '',
          },
          {
            action: actions.PERMISSION_ROLE_LIST,
            description: 'View lists of permission roles (including in API keys)',
            extraInformation: '',
          },
          {
            action: actions.PERMISSION_ROLE_GET,
            description: 'View permission details (API)',
            extraInformation: '',
          },
          {
            action: actions.PERMISSION_ROLE_UPDATE,
            description: 'Edit permission details',
            extraInformation: '',
          },
          {
            action: actions.PERMISSION_ROLE_DELETE,
            description: 'Delete permission details',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Audit',
        permissions: [
          {
            action: actions.AUDIT_TRACK_LIST,
            description: 'View audit trail',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Interface settings',
        permissions: [
          {
            action: actions.INTERFACE_SETTINGS_LIST,
            description: 'View interface settings list',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TABLE_SETUP_GET,
            description: 'View transaction table default setup',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TABLE_SETUP_UPDATE_COLUMNS,
            description: 'Edit default columns in transaction table',
            extraInformation: '',
          },
          {
            action: actions.TRANSACTION_TABLE_SETUP_UPDATE_ROW,
            description: 'Edit default rows in transaction table',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Data provider',
        permissions: [
          {
            action: actions.PROVIDER_MANAGEMENT,
            description: 'Edit data provider settings (API)',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Webhooks',
        permissions: [
          {
            action: actions.WEBHOOK_CONFIGURATION_VIEW,
            description: 'View webhooks configuration',
            extraInformation: '',
          },
          {
            action: actions.WEBHOOK_CONFIGURATION_MANAGE,
            description: 'Manage webhooks configuration',
            extraInformation: '',
          },
          {
            action: actions.WEBHOOK_TRIGGER_TEST,
            description: 'Trigger a webhook test delivery',
            extraInformation: '',
          },
        ],
      },
      {
        name: 'Tags',
        permissions: [
          {
            action: actions.TAG_CREATE,
            description: 'Create tags',
            extraInformation: '',
          },
          {
            action: actions.TAG_GET,
            description: 'View tag details',
            extraInformation: '',
          },
          {
            action: actions.TAG_LIST,
            description: 'View tag lists',
            extraInformation: '',
          },
          {
            action: actions.TAG_UPDATE_ACTIVATION,
            description: 'Activate/deactivate tags',
            extraInformation: '',
          },
          {
            action: actions.TAG_UPDATE,
            description: 'Edit tag details',
            extraInformation: '',
          },
          {
            action: actions.TEAM_CREATE,
            description: 'Create teams',
            extraInformation: 'Deprecated',
          },
          {
            action: actions.TEAM_GET,
            description: 'View team details',
            extraInformation: 'Deprecated',
          },
          {
            action: actions.TEAM_LIST,
            description: 'View team lists',
            extraInformation: 'Deprecated',
          },
          {
            action: actions.TEAM_UPDATE_ACTIVATION,
            description: 'Activate/deactivate teams',
            extraInformation: 'Deprecated',
          },
          {
            action: actions.TEAM_UPDATE,
            description: 'Edit team details',
            extraInformation: 'Deprecated',
          },
        ],
      },
    ],
  },
  {
    name: 'Pingwin',
    subCategories: [
      {
        name: 'Chat assistant',
        permissions: [
          {
            action: actions.PINGU_CHAT_USE,
            description: 'Use the Pingwin chat assistant',
            extraInformation: '',
          },
        ],
      },
    ],
  },
];

export const actionDescriptionMap: Partial<Record<Actions, string>> = permissionCategories
  .flatMap((category) => category.subCategories)
  .flatMap((subCategory) => subCategory.permissions)
  .reduce((acc, permission) => ({ ...acc, [permission.action]: permission.description }), {});

export default permissionCategories;
